import { useMemo } from 'react';
import { useClassManageStore } from '@/features/class/manage/classManageStore';
import type { ClassCategoryBulkAction } from '@/features/profile/classes/classCategoryTypes';
import { useProfileClassOrganizationStore } from '@/features/profile/classes/profileClassOrganizationStore';
import Modal from '@/shared/ui/Modal';
import ModalHeader from '@/shared/ui/ModalHeader';
import Button from '@/shared/ui/Button';
import { textStyles } from '@/shared/tokens/typography';
import { cn } from '@/shared/lib/cn';

interface ClassCategoryBulkConfirmModalProps {
  categoryId: string;
  action: ClassCategoryBulkAction;
  onClose: () => void;
  onConfirm: () => void;
  isPending?: boolean;
  error?: string;
}

export default function ClassCategoryBulkConfirmModal({
  categoryId,
  action,
  onClose,
  onConfirm,
  isPending = false,
  error,
}: ClassCategoryBulkConfirmModalProps) {
  const category = useProfileClassOrganizationStore((state) =>
    state.categories.find((item) => item.id === categoryId),
  );
  const classes = useClassManageStore((state) => state.classes);

  const affectedClasses = useMemo(() => {
    if (!category) {
      return [];
    }
    const idSet = new Set(category.classIds);
    return classes.filter((item) => idSet.has(item.id));
  }, [category, classes]);

  if (!category) {
    return null;
  }

  const isArchive = action === 'archive';
  const title = isArchive ? 'Archive category' : 'Delete category';
  const verb = isArchive ? 'archive' : 'permanently delete';
  const count = affectedClasses.length;

  return (
    <Modal
      onClose={onClose}
      zIndexClassName="z-[110]"
      className="flex w-full max-w-lg flex-col"
      ariaLabel={title}
    >
      <ModalHeader title={title} onClose={onClose} />

      <div className="space-y-4 px-4 py-4 sm:px-6">
        <p className={textStyles.body}>
          This will {verb} <span className="font-semibold text-gray-900">{category.name}</span>
          {count > 0 ? (
            <>
              {' '}
              and <span className="font-semibold text-gray-900">{count}</span> class
              {count === 1 ? '' : 'es'} still in this category:
            </>
          ) : (
            '.'
          )}
        </p>

        {count > 0 ? (
          <ul className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-gray-300 bg-gray-50/80 px-3 py-2">
            {affectedClasses.map((classItem) => (
              <li key={classItem.id} className="text-sm text-gray-800">
                {classItem.name}
              </li>
            ))}
          </ul>
        ) : null}

        <p className={cn(textStyles.caption, 'text-gray-600')}>
          Move any class out of this category first if you want to exclude it from this action.
        </p>
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-gray-300 px-4 py-3 sm:px-6">
        <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="button" variant="danger" onClick={onConfirm} disabled={isPending}>
          {isPending ? 'Working…' : isArchive ? 'Archive category' : 'Delete category'}
        </Button>
      </div>
    </Modal>
  );
}
